import * as React from "react"
import { forwardRef } from "react"
import { Loader2 } from "lucide-react"
import { Button as ShadButton } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger" | "ghost" | "light" | "outline"
  size?: "sm" | "md" | "lg" | "icon"
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = "primary",
      size = "md",
      isLoading = false,
      leftIcon,
      rightIcon,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "inline-flex items-center justify-center font-medium rounded-xl " +
      "transition-all duration-200 ease-out active:scale-[0.98] " +
      "disabled:opacity-50 disabled:pointer-events-none"

    const variants: Record<NonNullable<ButtonProps["variant"]>, string> = {
      primary:
        "text-white bg-gradient-to-r from-primary-500 to-primary-600 " +
        "hover:from-primary-400 hover:to-primary-500 " +
        "shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 " +
        "focus-visible:ring-primary-500/50",

      secondary:
        "bg-surface-800/80 text-surface-200 border border-surface-700/50 " +
        "hover:bg-surface-700/80 hover:border-surface-600/50 hover:text-surface-100 " +
        "focus-visible:ring-surface-500/50",
      
      light:
        "bg-white/80 text-surface-700 border border-surface-300/50 " +
        "hover:bg-white/90 hover:border-surface-400/50 hover:text-surface-900 " +
        "focus-visible:ring-surface-500/50",

      danger:
        "text-white bg-gradient-to-r from-danger-500 to-danger-600 " +
        "hover:from-danger-400 hover:to-danger-500 " +
        "shadow-lg shadow-danger-500/25 hover:shadow-danger-500/40 " +
        "focus-visible:ring-danger-500/50",

      ghost:
        "text-surface-400 hover:text-surface-100 hover:bg-surface-800/50 " +
        "focus-visible:ring-surface-500/50",

      outline:
        "border border-input bg-background shadow-sm hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-accent-foreground",
    }

    const sizes: Record<NonNullable<ButtonProps["size"]>, string> = {
      sm: "px-3 py-1.5 text-xs gap-1.5",
      md: "px-4 py-2.5 text-sm gap-2",
      lg: "px-6 py-3 text-base gap-2",
      icon: "h-9 w-9",
    }

    return (
      <ShadButton
        ref={ref}
        variant="ghost" // ❗ dùng shadcn như base, style ta override hoàn toàn
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          className
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          leftIcon
        )}

        {children}

        {!isLoading && rightIcon}
      </ShadButton>
    )
  }
)

Button.displayName = "Button"
